import {
  getRestaurantDetailsBySlug,
  type RestaurantDetails,
} from 'webReservations/restaurant/apiHelpers';
import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useRestaurantLoaderData } from 'webReservations/hooks/useRestaurantLoaderData';
import { LANDING_PATH } from '../paths';

export interface RestaurantContextState {
  restaurantDetails: RestaurantDetails;
  setRestaurantDetails: (restaurantDetails: RestaurantDetails) => void;
}

export const RestaurantContext = createContext<RestaurantContextState | null>(
  null,
);
RestaurantContext.displayName = 'Restaurant';

export const useRestaurantContext = () => useDefinedContext(RestaurantContext);

export const RestaurantContextProvider = ({ children }: PropsWithChildren) => {
  const { locationSlug, nameSlug } = useParams();
  const ref = useRef<RestaurantDetails>();
  const [, forceRender] = useReducer((n: number) => n + 1, 0);

  const {
    data: restaurantFromParams,
    isPending: isRestaurantFromParamsLoading,
  } = useQuery({
    queryKey: ['restaurantDetails', locationSlug, nameSlug],
    queryFn: async () =>
      getRestaurantDetailsBySlug(locationSlug as string, nameSlug as string),
    enabled: !!locationSlug && !!nameSlug,
    throwOnError: true,
  });

  useMemo(() => {
    if (restaurantFromParams) {
      ref.current = restaurantFromParams;
    }
  }, [restaurantFromParams]);

  const loaderRestaurant = useRestaurantLoaderData();

  useMemo(() => {
    if (loaderRestaurant) {
      ref.current = loaderRestaurant;
    }
  }, [loaderRestaurant]);

  const value = useMemo((): RestaurantContextState | null => {
    if (!ref.current) {
      return null;
    }
    return {
      restaurantDetails: ref.current,
      setRestaurantDetails: (nextRestaurantDetails) => {
        ref.current = nextRestaurantDetails;
        forceRender();
      },
    };
  }, [ref.current]);

  if (!value) {
    if (isRestaurantFromParamsLoading) {
      return null;
    }
    return <Navigate to={LANDING_PATH} />;
  }

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};
